import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BrandLockup from './BrandLockup';
import LanguageSwitcher from './LanguageSwitcher';
import { appTheme } from '../styles/theme';

const HIDDEN_ROUTES = new Set(['/preview', '/edit']);

const FooterBar = styled.footer`
  margin-top: auto;
  padding: 64px 24px 32px;
  background: ${appTheme.footerBg};
  color: ${appTheme.textOnDark};
`;

const Inner = styled.div`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const TopGrid = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: 1.4fr repeat(4, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BrandMark = styled.div`
  color: #fff;

  span {
    color: #fff;
  }
`;

const About = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${appTheme.textMutedOnDark};
  max-width: 28ch;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ColumnTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
`;

const FooterLink = styled(Link)`
  font-size: 0.8125rem;
  color: ${appTheme.textMutedOnDark};
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #fff;
  }
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 24px;
  border-top: 1px solid ${appTheme.borderOnDark};
`;

const Copy = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${appTheme.textMutedOnDark};
`;

const FooterLanguage = styled(LanguageSwitcher)`
  border-color: ${appTheme.borderOnDark};
  background: rgba(255, 255, 255, 0.06);
  color: ${appTheme.textOnDark};

  &:hover {
    border-color: rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const year = new Date().getFullYear();

  if (HIDDEN_ROUTES.has(pathname)) {
    return null;
  }

  return (
    <FooterBar>
      <Inner>
        <TopGrid>
          <BrandBlock>
            <BrandMark>
              <BrandLockup logoHeight={32} />
            </BrandMark>
            <About>{t('footer.about')}</About>
          </BrandBlock>
          <Column>
            <ColumnTitle>{t('footer.product')}</ColumnTitle>
            <FooterLink to="/">{t('footer.templates')}</FooterLink>
            <FooterLink to="/decks/new">{t('footer.createDeck')}</FooterLink>
            <FooterLink to="/decks">{t('footer.myDecks')}</FooterLink>
            <FooterLink to="/subscription">{t('footer.pricing')}</FooterLink>
          </Column>
          <Column>
            <ColumnTitle>{t('footer.resources')}</ColumnTitle>
            <FooterLink to="/">{t('footer.pitchTemplates')}</FooterLink>
            <FooterLink to="/subscription">{t('footer.plans')}</FooterLink>
          </Column>
          <Column>
            <ColumnTitle>{t('footer.company')}</ColumnTitle>
            <FooterLink to="/terms">{t('footer.terms')}</FooterLink>
            <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
          </Column>
          <Column>
            <ColumnTitle>{t('footer.legal')}</ColumnTitle>
            <FooterLink to="/terms">{t('footer.termsConditions')}</FooterLink>
            <FooterLink to="/privacy">{t('footer.privacyPolicy')}</FooterLink>
          </Column>
        </TopGrid>
        <BottomBar>
          <Copy>{t('footer.copyright', { year })}</Copy>
          <FooterLanguage compact />
        </BottomBar>
      </Inner>
    </FooterBar>
  );
};

export default Footer;
